<template>
  <div
    :id="formattedId"
    :class="
      bem({
        layoutStyle,
      })
    "
  >
    <div
      v-if="block.content"
      :class="bem('title')"
    >
      <GenericStructuredText
        :class="bem('title-content')"
        :content="block.content"
        copy-size="body-sm"
        cta-size="x-small"
      />
      <DatoCmsImage
        :class="bem('background-image')"
        :data="block.backgroundImage.responsiveImage"
        layout="fill"
        object-fit="cover"
        object-position="50% 50%"
        sizes="(max-width: 600px) 400px, 800px"
      />
    </div>
    <div :class="bem('post-container')">
      <div :class="bem('controls')">
        <CarouselArrow
          arrow="left"
          :is-disabled="leftArrowDisabled"
        />
        <CarouselArrow
          arrow="right"
          :is-disabled="rightArrowDisabled"
        />
      </div>
      <Swiper
        :class="bem('swiper')"
        v-bind="swiperOptions"
        @slide-change="handleSlideChanged"
      >
        <SwiperSlide
          v-for="(post, index) in posts"
          :key="post.id"
          :class="bem('slide')"
        >
          <ImpactCarouselItem
            :post="post"
            :mobile-layout="layoutStyle"
            :index="index"
            :parent-tracking-id="block.trackingId"
          />
        </SwiperSlide>
      </Swiper>
    </div>
  </div>
</template>

<script setup>
import { Image as DatoCmsImage } from 'vue-datocms'
import { Swiper, SwiperSlide } from 'swiper/vue'
import { Navigation } from 'swiper/modules'
const bem = useBEM('ImpactCarousel')

const props = defineProps({
  posts: { type: Array, required: true },
  block: { type: Object, required: true },
  rowPosition: { type: Number, default: 0 },
  uniqueId: { type: String, default: 'impact-carousel' },
})

const formattedId =
  'impact-carousel--' + props.uniqueId?.toLowerCase().replace(/\s+/g, '-')

const leftArrowDisabled = ref(true)
const rightArrowDisabled = ref(false)

function handleSlideChanged(swiper) {
  leftArrowDisabled.value = swiper.isBeginning
  rightArrowDisabled.value = swiper.isEnd
}

const layoutStyle = computed(() => {
  return props.block.layout
})

const modules = [Navigation]

const swiperOptions = ref({
  modules: modules,
  slidesPerView: props.block.layout === 'normal' ? 2.25 : 1.25,
  spaceBetween: 24,
  speed: 300,
  loop: false,
  breakpoints: {
    400: {
      slidesPerView: props.block.layout === 'normal' ? 2.25 : 1.75,
    },
    650: {
      slidesPerView: 2.5,
    },
    800: {
      slidesPerView: 'auto',
    },
  },
  navigation: {
    nextEl: `#${formattedId} [data-carousel-arrow="right"]`,
    prevEl: `#${formattedId} [data-carousel-arrow="left"]`,
  },
})
</script>

<style lang="postcss">
.ImpactCarousel {
  @apply w-screen flex flex-col md:flex-row;
}

.ImpactCarousel__title {
  @apply relative w-full md:w-[40vw] lg:w-[32vw] h-full md:min-h-[666px]
  py-10 px-[var(--outer-gutter)] flex-grow;

  @screen mdMax {
    @apply bg-black pb-0;

    picture {
      @apply hidden;
    }
  }

  @screen md {
    @apply p-20;
  }

  @screen full {
    padding-left: calc((100vw - var(--screen-size)) / 2 + var(--outer-gutter));
  }

  .GenericStructuredText__content {
    @apply gap-y-3;
  }
}

.ImpactCarousel__title-content {
  @apply relative z-[2] mdMax:w-[calc(100%_-_80px)];
}

.ImpactCarousel__post-container {
  @apply relative w-full md:w-[calc(100%_-_40vw)] lg:w-[calc(100%_-_32vw)] h-full bg-black
  flex items-center py-10 md:py-20;
}

.ImpactCarousel__swiper {
  @apply w-full px-[var(--outer-gutter)] lg:pl-[120px];
}

.ImpactCarousel__slide {
  @apply w-fit;
}

.ImpactCarousel__controls {
  @apply absolute z-[2] pointer-events-none
  flex items-center px-0 gap-x-2 bottom-full right-[var(--outer-gutter)];

  @screen md {
    @apply justify-between inset-0 pl-12 pr-[var(--outer-gutter)] lg:pr-[calc(var(--outer-gutter)_-_24px)];
  }
}

/**
  'Boring' Layout Modifiers
 */
@screen mdMax {
  .ImpactCarousel--layout-style-normal {
    .ImpactCarousel__post-container {
      @apply bg-white py-0 pt-4;
    }

    .ImpactCarousel__title {
      @apply py-0;

      picture {
        @apply hidden;
      }

      .GenericStructuredText__content--text-white {
        @apply text-black;
      }
    }
    .ImpactCarousel__controls {
      @apply py-0;
    }
  }
}
</style>
